"use client";

import React from "react";
import { motion } from "framer-motion";

export interface CtlMobileShowcaseBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    images?: string[];
}

const MobileMockup = ({ imageSrc, altText, delay, wrapperClassName = "" }: { imageSrc: string; altText: string; delay: number; wrapperClassName?: string }) => (
    <motion.div 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
        className={`w-full flex justify-center ${wrapperClassName}`}
    >
        <div className="relative w-full max-w-[280px] sm:max-w-[320px] aspect-[9/19] group">
            {/* Buttons */}
            <div className="absolute left-[-3px] top-[90px] w-[3px] h-[16px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors" />
            <div className="absolute left-[-3px] top-[120px] w-[3px] h-[32px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors" />
            <div className="absolute left-[-3px] top-[164px] w-[3px] h-[32px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors" />
            <div className="absolute right-[-3px] top-[140px] w-[3px] h-[48px] bg-gradient-to-l from-[#d1d1d6] to-[#a8a8b0] rounded-r-md transition-colors" />
            
            {/* Phone Frame Outer (Silver Edge) */}
            <div className="w-full h-full rounded-[48px] bg-gradient-to-br from-[#e5e5ea] via-[#b0b0b5] to-[#e5e5ea] p-[3px] shadow-[0_15px_35px_rgba(0,0,0,0.1)] transition-all duration-500 group-hover:shadow-[0_20px_45px_rgba(68,170,82,0.15)]">
                
                {/* Phone Frame Inner (Black Bezel) */}
                <div className="w-full h-full rounded-[45px] bg-black p-[4px] sm:p-[5px] relative overflow-hidden">
                    
                    {/* Top Speaker Grill */}
                    <div className="absolute top-[1px] sm:top-[1.5px] left-1/2 -translate-x-1/2 w-[30px] sm:w-[40px] h-[2px] bg-[#222] rounded-full z-20" />
                    
                    {/* Screen Image */}
                    <div className="w-full h-full rounded-[41px] sm:rounded-[40px] overflow-hidden bg-white relative">
                        <img 
                            src={imageSrc} 
                            alt={altText} 
                            className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 pointer-events-none" />
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

export default function CtlMobileShowcaseBlock({
    tag = "04 / RESPONSIVE SHOWCASE",
    title = "Bespoke Mobile Interface",
    description = "We translated complex data configurations, system metrics, and structured client testimonials into mobile-responsive layouts, keeping pixel alignment absolute.",
    images = [
        "/images/ctl_mobile_1.png",
        "/images/ctl_mobile_2.png",
        "/images/ctl_mobile_3.png"
    ]
}: CtlMobileShowcaseBlockProps) {
    const img1 = images[0] || "/images/ctl_mobile_1.png";
    const img2 = images[1] || "/images/ctl_mobile_2.png";
    const img3 = images[2] || "/images/ctl_mobile_3.png";

    return (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F8FAF8] border-b border-[#EAEAEA] relative overflow-hidden text-[#444] font-sans antialiased w-full">
            {/* Background Ambient Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#44aa52]/2 rounded-full blur-[140px] pointer-events-none" />
            
            <div className="container mx-auto">
                {/* Section Header */}
                <div className="max-w-3xl mb-16 md:mb-24">
                    {tag && (
                        <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">
                            {tag}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl md:text-5xl font-light text-[#1A1A1A] leading-tight">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                {/* Staggered Phone Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 items-start">
                    
                    {/* Column 1: Left */}
                    <div className="flex flex-col items-center w-full">
                        <MobileMockup imageSrc={img1} altText="CTL Mobile Screen 1" delay={0} />
                        <MobileMockup imageSrc={img2} altText="CTL Mobile Screen 4" delay={0.1} wrapperClassName="mt-12 md:mt-[224px] lg:mt-[320px]" />
                    </div>

                    {/* Column 2: Middle */}
                    <div className="flex flex-col items-center w-full">
                        <MobileMockup imageSrc={img2} altText="CTL Mobile Screen 2" delay={0.15} />
                        <MobileMockup imageSrc={img3} altText="CTL Mobile Screen 5" delay={0.25} wrapperClassName="mt-12 md:mt-[320px] lg:mt-[448px]" />
                    </div>

                    {/* Column 3: Right */}
                    <div className="flex flex-col items-center w-full md:pt-32 lg:pt-64">
                        <MobileMockup imageSrc={img3} altText="CTL Mobile Screen 3" delay={0.3} />
                        <MobileMockup imageSrc={img1} altText="CTL Mobile Screen 6" delay={0.4} wrapperClassName="mt-12 md:mt-[288px] lg:mt-[320px]" />
                    </div>

                </div>
            </div>
        </section>
    );
}
